import { formatDateFn } from "../../Core/Functions/formatDateFn";
import type {
  AvailabilityDayModel,
  AvailabilityResponseModel,
  AvailabilitySlotModel,
} from "../../Core/Models/BookingModels";

const slotTimePairs = [
  { hour: 10, label: "10:00 AM" },
  { hour: 13, label: "1:00 PM" },
  { hour: 16, label: "4:00 PM" },
];

export const buildFallbackAvailabilityFn = (
  referenceDate = new Date()
): AvailabilityResponseModel => {
  const days: AvailabilityDayModel[] = [];

  for (let index = 0; index < 21; index += 1) {
    const day = new Date(referenceDate);
    day.setHours(12, 0, 0, 0);
    day.setDate(referenceDate.getDate() + index);

    const isoDate = day.toISOString().slice(0, 10);
    const weekday = day.getDay();
    const isAvailable = weekday !== 0 && (index + 1) % 5 !== 0;

    const slots: AvailabilitySlotModel[] = isAvailable
      ? slotTimePairs.map((slot) => ({
          id: `${isoDate}-${slot.hour}`,
          date: isoDate,
          label: slot.label,
          isoStart: new Date(
            day.getFullYear(),
            day.getMonth(),
            day.getDate(),
            slot.hour,
            0,
            0
          ).toISOString(),
          durationMinutes: 90,
          isAvailable: true,
        }))
      : [];

    days.push({
      date: isoDate,
      label: formatDateFn(isoDate, {
        weekday: "short",
        month: "short",
        day: "numeric",
      }),
      isAvailable,
      slots,
    });
  }

  return {
    source: "fallback",
    generatedAt: new Date().toISOString(),
    days,
  };
};

export const normalizeAvailabilityFn = (
  value: unknown
): AvailabilityResponseModel | null => {
  if (!value || typeof value !== "object") {
    return null;
  }

  const response = value as Partial<AvailabilityResponseModel>;
  if (!Array.isArray(response.days)) {
    return null;
  }

  const days = response.days;
  if (!days.every(day => day && typeof day.date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(day.date)
    && typeof day.label === "string" && typeof day.isAvailable === "boolean" && Array.isArray(day.slots)
    && day.slots.every(slot => slot && typeof slot.id === "string" && slot.date === day.date
      && typeof slot.label === "string" && typeof slot.isoStart === "string" && Number.isFinite(Date.parse(slot.isoStart))
      && Number.isFinite(slot.durationMinutes) && slot.durationMinutes > 0 && slot.isAvailable === true)
    && day.isAvailable === (day.slots.length > 0))) return null;

  return {
    source: response.source === "api" ? "api" : "fallback",
    generatedAt: response.generatedAt || new Date().toISOString(),
    days,
  };
};

export const buildCalendarWeeksFn = (days: AvailabilityDayModel[]) => {
  if (!days.length) {
    return [];
  }

  const firstWeekday = new Date(`${days[0].date}T12:00:00`).getDay();
  const paddedDays: Array<AvailabilityDayModel | null> = [
    ...Array.from({ length: firstWeekday }, () => null),
    ...days,
  ];

  const weeks: Array<Array<AvailabilityDayModel | null>> = [];
  for (let index = 0; index < paddedDays.length; index += 7) {
    weeks.push(paddedDays.slice(index, index + 7));
  }

  return weeks;
};

export const findSelectedDayFn = (
  days: AvailabilityDayModel[],
  selectedDate: string
) => days.find((day) => day.date === selectedDate) ?? null;

export const deriveSelectedSlotLabelFn = (
  selectedDate: string,
  selectedTime: string
) =>
  selectedDate && selectedTime
    ? `${formatDateFn(selectedDate)} at ${selectedTime}`
    : "Select a date and time to continue.";
